import { makeNumber } from '@/helpers/chartFormat'
import { roundEstimate } from '@/helpers/insightsMetrics'

import { metricState, type InsightsMetricState } from './insightsState'
import type { BuyerInsightsQuery } from '../../reviews.generated'

export interface BuyerInsightsView {
  profileViews: string
  clickOuts: string
  visitingCompanies: string
  states: {
    clickout: InsightsMetricState
    visiting: InsightsMetricState
  }
}

type BuyerResults = NonNullable<NonNullable<BuyerInsightsQuery['manage']>['insights']>['results']
type BuyerResponse = NonNullable<BuyerResults[number]['response']>
type ClickoutResponse = Extract<BuyerResponse, { __typename?: 'ManageInsightsClickoutRateResponse' }>
type VisitingCompaniesResponse = Extract<BuyerResponse, { __typename?: 'ManageInsightsVisitingCompaniesResponse' }>

const CLICKOUT_METRIC = 'clickout-rate'
const VISITING_METRIC = 'visiting-companies'

const countsState = (state: InsightsMetricState): InsightsMetricState => (state === 'below-guardrail' ? 'ready' : state)

const formatCount = (value: number | null | undefined, locale: string): string => (value == null ? '—' : makeNumber(locale)(value))

const dedupeByPeriod = <T extends { period?: string | null }>(rows: readonly T[]): T[] => {
  const byPeriod = new Map<string, T>()
  for (const row of rows) if (row.period) byPeriod.set(row.period, row)
  return [...byPeriod.values()]
}

const averageBy = <T>(rows: readonly T[], pick: (row: T) => number | null | undefined): number | null => {
  const values = rows.map(pick).filter((value): value is number => value != null)
  if (!values.length) return null
  return values.reduce((sum, value) => sum + value, 0) / values.length
}

const pickResponses = (results: BuyerResults) => {
  let clickout: ClickoutResponse | undefined
  let visiting: VisitingCompaniesResponse | undefined
  for (const item of results) {
    const response = item.response
    if (!response || item.status !== 200) continue
    if (response.__typename === 'ManageInsightsClickoutRateResponse') clickout = response
    else if (response.__typename === 'ManageInsightsVisitingCompaniesResponse') visiting = response
  }
  return { clickout, visiting }
}

export const mapBuyerInsights = (query: BuyerInsightsQuery | null | undefined, { locale }: { locale: string }): BuyerInsightsView => {
  const results = query?.manage?.insights?.results ?? []
  const { clickout, visiting } = pickResponses(results)
  const byMetric = new Map(results.map((item) => [item.metric, item]))

  const clickoutRows = dedupeByPeriod(clickout?.rows ?? [])
  const visitingRows = dedupeByPeriod(visiting?.rows ?? [])

  return {
    profileViews: formatCount(roundEstimate(averageBy(clickoutRows, (row) => row.pageviewsEstimated)), locale),
    clickOuts: formatCount(roundEstimate(averageBy(clickoutRows, (row) => row.clickouts)), locale),
    visitingCompanies: formatCount(roundEstimate(averageBy(visitingRows, (row) => row.demaskedCompanies)), locale),
    states: {
      clickout: countsState(metricState(byMetric.get(CLICKOUT_METRIC), clickoutRows.length)),
      visiting: metricState(byMetric.get(VISITING_METRIC), visitingRows.length),
    },
  }
}
